// src/screens/AnalyticsOverviewScreen.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Screen from '../Screen';
import api from '../api';

import backIcon from '../assets/backspace-icon.png';
import downIcon from '../assets/dropdown-icon.png'; // ✅ if you don't have it, we fallback to "▼" text
import './AnalyticsOverviewScreen.css';

import DateRangeDrawer, { labelForRange } from '../components/popups/DateRangeDrawer';

export default function AnalyticsOverviewScreen({ user }) {
  const navigate = useNavigate();

  const [range, setRange] = useState('7d'); // 'today' | '7d' | '30d' | 'all'
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [blocked, setBlocked] = useState(null);
  const [error, setError] = useState('');

  const title = useMemo(() => labelForRange(range), [range]);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      setLoading(true);
      setError('');
      setBlocked(null);

      try {
        const res = await api.get(`/analytics/me?range=${range}`);
        if (!alive) return;
        setData(res?.data || null);
      } catch (e) {
        if (!alive) return;

        const status = e?.response?.status;
        const payload = e?.response?.data;

        if (status === 403 && payload?.code === 'ANALYTICS_NOT_ALLOWED') {
          setBlocked({
            planId: payload?.planId || 'FREE',
            message: payload?.message || 'Analytics is available on Premium and Premium+ plans.',
          });
          setData(null);
        } else if (status === 401) {
          setError('Please login again.');
        } else {
          setError(payload?.message || 'Failed to load analytics.');
        }
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => { alive = false; };
  }, [range]);

  const ov = data?.overview || {};

  return (
    <Screen>
      <div className="analytics-overview-screen">
        <div className="analytics-overview-topbar">
          <img
            src={backIcon}
            alt="Back"
            className="analytics-overview-back"
            onClick={() => navigate(-1)}
          />
          <h2 className="analytics-overview-title">Overview</h2>
          <div className="analytics-overview-spacer" />
        </div>

        <div className="analytics-overview-body">
          {/* ✅ New: range dropdown trigger */}
          <div className="analytics-overview-rangeRow">
            <button
              type="button"
              className="analytics-overview-rangeBtn"
              onClick={() => setDrawerOpen(true)}
              aria-label="Change date range"
            >
              <span className="analytics-overview-rangeText">{title}</span>

              {/* icon fallback if file missing */}
              {downIcon ? (
                <img
                  src={downIcon}
                  alt=""
                  className="analytics-overview-rangeIcon"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : (
                <span className="analytics-overview-rangeCaret">▼</span>
              )}
            </button>
          </div>

          {loading && (
            <div className="analytics-overview-loader">
              <div className="analytics-overview-spinner" />
              <p>Loading your stats…</p>
            </div>
          )}

          {!loading && blocked && (
            <div className="analytics-overview-card analytics-overview-blocked">
              <h3>Premium feature</h3>
              <p>{blocked.message}</p>
              <div className="analytics-overview-blocked-row">
                <span className="analytics-overview-pill">Your plan: {blocked.planId}</span>
                <button className="analytics-overview-upgrade" onClick={() => navigate('/plans')}>
                  Upgrade
                </button>
              </div>
            </div>
          )}

          {!loading && !blocked && error && (
            <div className="analytics-overview-card analytics-overview-error">
              <h3>Couldn’t load</h3>
              <p>{error}</p>
              <button className="analytics-overview-upgrade" onClick={() => window.location.reload()}>
                Retry
              </button>
            </div>
          )}

          {!loading && !blocked && !error && data && (
            <>
              <div className="analytics-overview-grid">
                <div className="analytics-overview-metric">
                  <div className="label">Posts</div>
                  <div className="value">{ov.postsCreated ?? 0}</div>
                </div>
                <div className="analytics-overview-metric">
                  <div className="label">Likes</div>
                  <div className="value">{ov.likesReceived ?? 0}</div>
                </div>
                <div className="analytics-overview-metric">
                  <div className="label">Comments</div>
                  <div className="value">{ov.commentsReceived ?? 0}</div>
                </div>
                <div className="analytics-overview-metric">
                  <div className="label">Shares</div>
                  <div className="value">{ov.sharesReceived ?? 0}</div>
                </div>
                <div className="analytics-overview-metric">
                  <div className="label">Saves</div>
                  <div className="value">{ov.savesReceived ?? 0}</div>
                </div>
                <div className="analytics-overview-metric">
                  <div className="label">Reposts</div>
                  <div className="value">{ov.repostsReceived ?? 0}</div>
                </div>

                {/* ✅ NEW: Followers + Following (below Saves/Reposts) */}
                <div className="analytics-overview-metric">
                  <div className="label">Followers</div>
                  <div className="value">{ov.followersCount ?? 0}</div>
                </div>
                <div className="analytics-overview-metric">
                  <div className="label">Following</div>
                  <div className="value">{ov.followingCount ?? 0}</div>
                </div>
              </div>

              <div className="analytics-overview-card">
                <div className="analytics-overview-card-head">
                  <h3>Earnings (Tips)</h3>
                  <span className="analytics-overview-pill">Mainnet</span>
                </div>

                <div className="analytics-overview-earnings-row">
                  <div className="earn">
                    <div className="label">Total received</div>
                    <div className="value">{Number(ov.tipsReceivedTotalPi ?? 0).toFixed(6)} Pi</div>
                  </div>

                  <div className="earn">
                    <div className="label">Tips count</div>
                    <div className="value">{ov.tipsReceivedCount ?? 0}</div>
                  </div>

                  <div className="earn">
                    <div className="label">Avg per tip</div>
                    <div className="value">{Number(ov.tipsReceivedAvgPi ?? 0).toFixed(6)} Pi</div>
                  </div>
                </div>

                <div className="analytics-overview-note">
                  Tips include tips received on your posts and stories.
                </div>
              </div>

              <div className="analytics-overview-footer">
                Logged in as <b>@{user?.username || '—'}</b>
              </div>
            </>
          )}
        </div>

        {/* ✅ Drawer */}
        <DateRangeDrawer
          open={drawerOpen}
          value={range}
          onClose={() => setDrawerOpen(false)}
          onChange={(v) => setRange(v)}
          title="Date ranges"
        />
      </div>
    </Screen>
  );
}
